"use client";

import { useState } from "react";
import Image from "next/image";
import { IoCopyOutline } from "react-icons/io5";

import { profileData, socialMedia } from "@/data";
import { ConfettiAnimation } from "./ConfettiAnimation";

type ContactState = {
  name: string;
  email: string;
  message: string;
  website: string;
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Contact = () => {
  const [form, setForm] = useState<ContactState>({
    name: "",
    email: "",
    message: "",
    website: "",
  });
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState<{
    type: "idle" | "success" | "error";
    message: string;
  }>({ type: "idle", message: "" });
  const [showConfetti, setShowConfetti] = useState(false);

  const updateField = (
    field: keyof ContactState,
    value: ContactState[keyof ContactState],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCopyEmail = async () => {
    await navigator.clipboard.writeText(profileData.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus({ type: "idle", message: "" });

    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 8000);

    if (form.website.trim()) {
      return;
    }

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({
        type: "error",
        message: "Please fill out name, email, and message.",
      });
      return;
    }

    if (!emailRegex.test(form.email.trim())) {
      setStatus({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return;
    }

    setStatus({
      type: "success",
      message: "Thanks for reaching out! We'll get back to you soon.",
    });

    setForm({
      name: "",
      email: "",
      message: "",
      website: "",
    });

    try {
      await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
          website: form.website.trim(),
        }),
      }).then((response) => {
        if (!response.ok) {
          throw new Error("Failed to send email");
        }
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: "Failed to send message. Please try again.",
      });
      console.error("Contact form error:", error);
    }
  };

  return (
    <section id="contact" className="py-20">
      <ConfettiAnimation isVisible={showConfetti} />
      <h2 className="heading">
        Let&apos;s Build <span className="text-purple">Something Together</span>
      </h2>
      <p className="text-white-200 text-center mt-4 max-w-2xl mx-auto">
        Have a project in mind or want to collaborate? Feel free to reach out.
        I&apos;m currently available for freelance work and new opportunities.
      </p>

      <div className="mt-8 sm:mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        <div className="rounded-2xl border border-white/10 bg-black-200 p-4 sm:p-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1">
            <span className="text-sm text-emerald-300">
              🟢 Available for Freelance
            </span>
          </div>

          <h2 className="text-white text-2xl font-semibold mt-5">
            Contact Information
          </h2>

          <div className="mt-5 rounded-xl border border-white/10 bg-black-100 p-4">
            <p className="text-white-200 text-sm">Email</p>
            <div className="flex items-center justify-between gap-3 mt-2">
              <p className="text-white break-all">{profileData.email}</p>
              <button
                type="button"
                onClick={handleCopyEmail}
                className="inline-flex items-center gap-2 rounded-md border border-white/20 px-3 py-2 text-sm text-white hover:bg-white/10 transition"
              >
                <IoCopyOutline />
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="text-xs text-white-200 mt-3">
              Typically replies within 24 hours.
            </p>
          </div>

          <div className="mt-6">
            <p className="text-white text-sm font-semibold">Connect with me</p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {socialMedia.map((info, index) => {
                const isMailLink = info.link.startsWith("mailto:");

                return (
                  <div key={info.id} className="flex items-center gap-2">
                    <a
                      href={info.link}
                      target={isMailLink ? undefined : "_blank"}
                      rel={isMailLink ? undefined : "noopener noreferrer"}
                      aria-label={info.alt}
                      className="interactive-lift inline-flex items-center gap-2 rounded-lg border border-white/10 bg-black-100 px-3 py-2 text-sm text-white hover:bg-white/10 transition"
                    >
                      <Image
                        src={info.img}
                        alt={info.alt}
                        width={16}
                        height={16}
                      />
                      <span>{info.alt}</span>
                    </a>
                    {index !== socialMedia.length - 1 && (
                      <span className="text-white/35">|</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-white/10 bg-black-200 p-4 sm:p-6 space-y-3 sm:space-y-4"
        >
          <h2 className="text-white text-2xl font-semibold">Send a Message</h2>

          <div>
            <label htmlFor="name" className="text-white-200 text-sm">
              Name
            </label>
            <input
              id="name"
              type="text"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              className="mt-2 w-full rounded-md border border-white/10 bg-black-100 px-4 py-3 text-white placeholder:text-white-200/70 focus:outline-none focus:ring-2 focus:ring-purple/50"
              placeholder="Enter your name"
            />
          </div>

          <div>
            <label htmlFor="email" className="text-white-200 text-sm">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
              className="mt-2 w-full rounded-md border border-white/10 bg-black-100 px-4 py-3 text-white placeholder:text-white-200/70 focus:outline-none focus:ring-2 focus:ring-purple/50"
              placeholder="Enter your email address"
            />
          </div>

          <div className="hidden">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              type="text"
              value={form.website}
              onChange={(e) => updateField("website", e.target.value)}
              autoComplete="off"
              tabIndex={-1}
            />
          </div>

          <div>
            <label htmlFor="message" className="text-white-200 text-sm">
              Message
            </label>
            <textarea
              id="message"
              value={form.message}
              onChange={(e) => updateField("message", e.target.value)}
              rows={5}
              className="mt-2 w-full rounded-md border border-white/10 bg-black-100 px-4 py-3 text-white placeholder:text-white-200/70 focus:outline-none focus:ring-2 focus:ring-purple/50 resize-none"
              placeholder="Tell me about your project or idea..."
            />
          </div>

          <button
            type="submit"
            className="interactive-lift button-glow w-full rounded-md border border-white/25 px-5 py-3 text-white hover:bg-white/10 transition"
          >
            Start a Conversation →
          </button>

          {status.type !== "idle" && (
            <p
              className={`text-sm ${
                status.type === "error" ? "text-red-300" : "text-emerald-300"
              }`}
            >
              {status.message}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default Contact;
